import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { invalidateLocalPowerCache } from '../services/edge-client';

// ===== User Interface =====
export interface User {
  id: string;
  username: string;
  email: string;
  firstName?: string;
  lastName?: string;
  roles: string[];
  // FTTH-specific fields from JWT
  accountId?: number | string;       // e.g. 5922 or LOCAL-TEST-1001
  partnerId?: number | string;       // same as accountId, used in API URLs
  groups?: string[];        // e.g. ["/Team_Contractor_5922_Members"]
  // Sub-User & Permissions fields
  authType?: 'SSO' | 'LOCAL';
  permissions?: string[];
  forcePasswordChange?: boolean;
  isOwner?: boolean;
  role?: string;
}

// ===== Auth State =====
export interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  // Non-sensitive credentials for username/partner auto-fill
  savedUsername: string | null;
  savedPartnerId: string | null;
  // Partner ID extracted from JWT (AccountId claim) or sub-user login
  partnerId: number | string | null;
  rememberMe: boolean;
  logoutConfirmOpen: boolean;
  /**
   * A unique ID generated at every successful login.
   * Used to scope React Query cache keys so that data from
   * user A can never bleed into user B's session, even if
   * the browser localStorage retains stale auth data.
   */
  sessionId: string | null;
  portalAuthWarning: { message: string; timestamp: number } | null;

  // Actions
  setUser: (user: User | null) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setAccessToken: (token: string) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setPartnerId: (id: number | string) => void;
  setSavedCredentials: (username: string, partnerId?: string) => void;
  setRememberMe: (rememberMe: boolean) => void;
  setLogoutConfirmOpen: (open: boolean) => void;
  setSessionId: (id: string) => void;
  setPortalAuthWarning: (warning: { message: string; timestamp: number } | null) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      savedUsername: null,
      savedPartnerId: null,
      partnerId: null,
      rememberMe: true,
      logoutConfirmOpen: false,
      sessionId: null,
      portalAuthWarning: null,

      setUser: (user) => {
        try { invalidateLocalPowerCache(); } catch (e) {}
        const newSessionId = 'sess_' + Date.now() + '_' + Math.random().toString(36).substring(2, 7);
        set({ user, isAuthenticated: !!user, sessionId: newSessionId });
      },
      setTokens: (accessToken, refreshToken) => {
        try { invalidateLocalPowerCache(); } catch (e) {}
        const newSessionId = 'sess_' + Date.now() + '_' + Math.random().toString(36).substring(2, 7);
        set({ accessToken, refreshToken, isAuthenticated: true, sessionId: newSessionId, portalAuthWarning: null });
      },
      setAccessToken: (accessToken) => set({ accessToken }),
      setLoading: (isLoading) => set({ isLoading }),
      setError: (error) => set({ error }),
      setPartnerId: (partnerId) => set({ partnerId }),
      setSavedCredentials: (savedUsername, savedPartnerId) =>
         set((state) => ({ savedUsername, savedPartnerId: savedPartnerId !== undefined ? savedPartnerId : state.savedPartnerId })),
      setRememberMe: (rememberMe) => set({ rememberMe }),
      setLogoutConfirmOpen: (logoutConfirmOpen) => set({ logoutConfirmOpen }),
      setSessionId: (sessionId) => set({ sessionId }),
      setPortalAuthWarning: (portalAuthWarning) => set({ portalAuthWarning }),
      logout: () => {
        try { invalidateLocalPowerCache(); } catch (e) {}
        try {
          // Selectively remove authentication and session-scoped keys only
          sessionStorage.removeItem('ftth_push_banner_dismissed');
          sessionStorage.removeItem('ftth_session_id');
          sessionStorage.removeItem('portal_token');
          sessionStorage.removeItem('portal_session');
          sessionStorage.removeItem('oauth_state');
        } catch (e) {}
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          error: null,
          partnerId: null,
          savedUsername: null,
          savedPartnerId: null,
          rememberMe: true,
          logoutConfirmOpen: false,
          sessionId: null,
          portalAuthWarning: null,
        });
      },
    }),
    {
      name: 'ftth-auth-storage',
      // Persist only non-sensitive identifiers when rememberMe is true. Passwords are NEVER persisted.
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        partnerId: state.partnerId,
        rememberMe: state.rememberMe,
        savedUsername: state.rememberMe ? state.savedUsername : null,
        savedPartnerId: state.rememberMe ? state.savedPartnerId : null,
        sessionId: state.sessionId,
        portalAuthWarning: state.portalAuthWarning,
      }),
    },
  ),
);

// ===== Theme State =====
export interface ThemeState {
  theme: 'light' | 'dark';
  language: 'ar' | 'en';
  sidebarOpen: boolean;

  setTheme: (theme: 'light' | 'dark') => void;
  setLanguage: (language: 'ar' | 'en') => void;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      theme: 'dark',
      language: 'ar',
      sidebarOpen: true,

      setTheme: (theme) => set({ theme }),
      setLanguage: (language) => set({ language }),
      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
    }),
    {
      name: 'theme-storage',
    },
  ),
);

// ===== Dashboard State =====
export interface DashboardState {
  customersCount: number;
  subscriptionsCount: number;
  tasksCount: number;
  requestsCount: number;
  ticketsCount: number;
  walletBalance: number;
  isLoading: boolean;
  lastUpdated: Date | null;

  setStats: (stats: Partial<DashboardState>) => void;
  setLoading: (loading: boolean) => void;
}

export const useDashboardStore = create<DashboardState>((set) => ({
  customersCount: 0,
  subscriptionsCount: 0,
  tasksCount: 0,
  requestsCount: 0,
  ticketsCount: 0,
  walletBalance: 0,
  isLoading: false,
  lastUpdated: null,

  setStats: (stats) => set(stats),
  setLoading: (isLoading) => set({ isLoading }),
}));

export * from './notificationStore';

